import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Section from '@/components/ui/Section/Section';
import SectionHeader from '@/components/ui/SectionHeader/SectionHeader';
import ChapterBadge from '@/components/ui/ChapterBadge/ChapterBadge';
import AnnotationLabel from '@/components/ui/AnnotationLabel/AnnotationLabel';
import Icon from '@/components/ui/Icon/Icon';
import ReportCover from '@/components/shirabe/ReportCover/ReportCover';
import { CHAPTERS, type ChapterId } from '@/lib/chapters';
import styles from './ShirabeSection.module.css';

const SAMPLE_ADDRESS = '東京都世田谷区 桜上水 三丁目 12-3';

// 章プレビュー画像 = サンプルレポート (/report/sample) の各章の先頭を表紙と同じ縦横比で撮影したもの。
// scripts/capture-chapters.py で再生成する。ファイル名 (= 章 id) で拾うので、画像の無い章は
// 表紙のまま「サンプルで読む」リンクだけを出す (ビルドは落とさない)。
const PREVIEW_FILES = import.meta.glob<string>('@/assets/chapters/*.webp', {
  eager: true,
  import: 'default',
});
const PREVIEW_FOR: Partial<Record<ChapterId, string>> = {};
for (const [path, url] of Object.entries(PREVIEW_FILES)) {
  const id = path.split('/').pop()?.replace(/\.webp$/, '') as ChapterId | undefined;
  if (id) PREVIEW_FOR[id] = url;
}

// ホバー/フォーカス時に先読みして、クリック時の切り替えを待たせない。
const preloaded = new Set<string>();
function preload(url: string | undefined) {
  if (!url || preloaded.has(url)) return;
  preloaded.add(url);
  const img = new Image();
  img.src = url;
}

export default function ShirabeSection() {
  // null = 表紙。章をクリックするとその章のプレビューに切り替わり、もう一度クリックで表紙に戻る。
  const [selected, setSelected] = useState<ChapterId | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const select = useCallback((id: ChapterId) => {
    setSelected((cur) => (cur === id ? null : id));
    // 1 カラム (スマホ) では一覧がプレビューの下に来るので、切り替えた面が見えていなければ寄せる。
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const headerH = 86;
    if (rect.top < headerH || rect.bottom > window.innerHeight) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    }
  }, []);

  const selectedIndex = selected ? CHAPTERS.findIndex((c) => c.id === selected) : -1;
  const selectedMeta = selectedIndex >= 0 ? CHAPTERS[selectedIndex] : null;
  const previewUrl = selected ? PREVIEW_FOR[selected] : undefined;
  const sampleHref = selected ? `/report/sample#${selected}` : '/report/sample';

  return (
    <Section id="report" aria-labelledby="report-title">
      <SectionHeader
        overline="チェックする八つの章"
        title="何がわかる？"
        titleId="report-title"
        lead="災害リスクから暮らしやすさ、将来の見通しまで。ひとつの土地を、いろいろな角度から確かめられます。"
      />

      <div className={styles.grid}>
        <div>
          <div ref={stageRef} className={styles.stage} aria-live="polite">
            {selectedMeta && previewUrl ? (
              <figure
                key={selectedMeta.id}
                className={styles.page}
                aria-label={`「${selectedMeta.title}」の章のプレビュー`}
              >
                <img
                  className={styles.pageImg}
                  src={previewUrl}
                  alt={`立地レポート ${selectedIndex + 1} 章「${selectedMeta.title}」の冒頭。サンプル住所のデータで作成したもの。`}
                />
                <figcaption className={styles.pageCaption}>
                  <AnnotationLabel
                    en={`${selectedIndex + 1}章`}
                    label={selectedMeta.shortTitle}
                    tick="left"
                  />
                </figcaption>
              </figure>
            ) : (
              <ReportCover
                address={SAMPLE_ADDRESS}
                generatedAt={new Date().toISOString()}
                preview
              />
            )}
            <p className={styles.stageNote}>
              <Link to={sampleHref} className={styles.sampleLink}>
                {selectedMeta ? `サンプルで「${selectedMeta.shortTitle}」の章を読む` : 'サンプルを読む'}
                <Icon name="arrow-right" size={14} />
              </Link>
              {selectedMeta && (
                <button
                  type="button"
                  className={styles.coverBack}
                  onClick={() => setSelected(null)}
                >
                  表紙に戻る
                </button>
              )}
            </p>
          </div>
        </div>

        <div>
          <p className={styles.body}>
            「立地レポート」は、八つの章でできたWebのレポートです。災害リスク、地盤と造成、用途と建築可否、価格相場、暮らしの環境、将来の見通し。ひとつの土地を、いろんな角度からまるごと読み解いて、わかりやすい言葉でお伝えします。
          </p>

          <p className={styles.hint} id="report-chapters-hint">
            章をえらぶと、その章のプレビューが見られます。
          </p>
          <ol
            className={styles.chapters}
            aria-label="立地レポートの構成"
            aria-describedby="report-chapters-hint"
          >
            {CHAPTERS.map((chapter, index) => {
              const active = chapter.id === selected;
              return (
                <li key={chapter.id} className={active ? styles.active : undefined}>
                  <button
                    type="button"
                    className={styles.chBtn}
                    aria-pressed={active}
                    onClick={() => select(chapter.id)}
                    onPointerEnter={() => preload(PREVIEW_FOR[chapter.id])}
                    onFocus={() => preload(PREVIEW_FOR[chapter.id])}
                  >
                    <ChapterBadge number={index + 1} active={active} />
                    <span className={styles.chTitle}>{chapter.title}</span>
                    <span className={styles.chHint} aria-hidden="true">
                      <Icon name="arrow-right" size={14} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          <p className={styles.note}>
            ブラウザで開いて、章を行き来しながら、いつでもゆっくり読み返せます。会員登録もインストールも、いりません。
          </p>
        </div>
      </div>
    </Section>
  );
}
